import Link from "next/link";
import { FooterWhatsApp } from "./FooterWhatsApp";
import { FooterHomeLink } from "./FooterHomeLink";
import { ArrowUp, ArrowUpRight, Clock, Mail, MapPin, Phone } from "lucide-react";
import { footerNav, site } from "@/lib/site";
import { Container } from "@/components/ui/Container";
import { FooterFrame } from "./FooterFrame";
import { FooterCream } from "./FooterCream";
import { Logo } from "@/components/ui/Logo";
import { GlobeArcs } from "@/components/ui/decor";
import {
  LinkedinIcon,
  InstagramIcon,
  FacebookIcon,
  // YoutubeIcon,
  // XIcon,
  WhatsappIcon,
} from "@/components/ui/social-icons";

const socials = [
  { href: site.socials.linkedin, icon: LinkedinIcon, label: "LinkedIn" },
  { href: site.socials.instagram, icon: InstagramIcon, label: "Instagram" },
  { href: site.socials.facebook, icon: FacebookIcon, label: "Facebook" },
  // { href: site.socials.youtube, icon: YoutubeIcon, label: "YouTube" },
  // { href: site.socials.x, icon: XIcon, label: "X" },
  { href: site.socials.whatsapp, icon: WhatsappIcon, label: "WhatsApp" },
];

const contact = [
  { icon: Mail, label: "Email", value: site.email, href: `mailto:${site.email}` },
  { icon: Phone, label: "Phone", value: site.phone, href: `tel:${site.phone.replace(/\s/g, "")}` },
  { icon: MapPin, label: "Address", value: `${site.address.line1}, ${site.address.line2}`, href: site.mapUrl },
  { icon: Clock, label: "Hours", value: site.hours },
];

export function Footer({ courseLinks: popular }: { courseLinks: { label: string; href: string }[] }) {
  const courseLinks = [
    ...popular,
    { label: "View All Courses", href: "/courses" },
  ];

  return (
    <FooterFrame cream={<FooterCream courseLinks={courseLinks} />}>
    <footer className="relative mt-auto overflow-hidden bg-brand-900 text-cream/80">
      <GlobeArcs className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 text-white/5" />
      <div className="absolute inset-x-0 top-0 h-px bg-gold-500/40" />

      <Container className="relative pt-14 sm:pt-16">
        {/* top: brand + quick enquiry */}
        <div className="flex flex-col gap-8 border-b border-white/10 pb-10 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-md space-y-4">
            <Logo variant="onDark" />
            <p className="text-sm leading-relaxed text-cream/70">
              Empowering learners with practical skills, expert mentorship and career-focused education.
            </p>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-5 sm:p-6 lg:max-w-md">
            <div className="font-display text-xl font-semibold text-white">Have a question?</div>
            <p className="mt-1 text-sm text-cream/70">Talk to an advisor about courses, batches and fees.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                href="/contact?tab=enquiry"
                className="inline-flex items-center gap-2 rounded-full bg-gold-400 px-5 py-2.5 text-sm font-semibold text-brand-900 transition-colors hover:bg-gold-300"
              >
                Enquiry now <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                Contact us
              </Link>
            </div>
          </div>
        </div>

        {/* link columns */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 py-12 md:grid-cols-3 lg:grid-cols-[0.8fr_1fr_1.2fr_1.4fr] lg:gap-x-10">
          <div className="space-y-8">
            <FooterCol title="Company" links={footerNav.company} />
          </div>

          <FooterCol title="Quick access" links={footerNav.access} />

          <div className="col-span-2 md:col-span-1">
            <FooterCol title="Courses" links={courseLinks} />
          </div>

          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <div className="relative flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-gold-300">Get in touch</h3>
              {/* out of the flow (margin, not a transform, so the floating fixed button keeps working) so the 44px button never makes this heading sit lower than the other columns' */}
              <div className="absolute right-0 top-1/2 -mt-[22px]">
                <FooterWhatsApp />
              </div>
            </div>
            <ul className="mt-4 space-y-3 text-sm">
              {contact.map(({ icon: Icon, label, value, href }) => {
                const body = (
                  <>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/5 text-gold-400 transition-colors group-hover:bg-gold-400 group-hover:text-brand-900">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] uppercase tracking-wider text-cream/50">{label}</span>
                      <span className="block break-words text-cream/90">{value}</span>
                    </span>
                  </>
                );
                return (
                  <li key={label}>
                    {href ? (
                      <a href={href} {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="group flex items-start gap-3 transition-colors hover:text-white">{body}</a>
                    ) : (
                      <div className="group flex items-start gap-3">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="mt-5 flex flex-wrap gap-2">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-cream/75 transition-all hover:-translate-y-0.5 hover:border-gold-400 hover:bg-gold-400 hover:text-brand-900"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* bottom bar */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 py-6 text-xs text-cream/55 sm:flex-row">
          <p className="text-center sm:text-left">
            © {new Date().getFullYear()}{" "}
            <FooterHomeLink className="font-semibold text-gold-300 underline underline-offset-2 transition-colors hover:text-gold-400">{site.name}</FooterHomeLink>. All rights reserved. Powered by <a href="https://www.ammrs.in/" target="_blank" rel="noopener noreferrer" className="font-semibold text-gold-300 underline underline-offset-2 hover:text-gold-400">AMMRS</a>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link href="/terms" className="hover:text-white">Terms</Link>
            <Link href="/privacy" className="hover:text-white">Privacy Policy</Link>
            <span className="font-script text-base text-gold-300/90">{site.tagline}</span>
            <a
              href="#main"
              aria-label="Back to top"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-cream/75 transition-colors hover:border-gold-400 hover:bg-gold-400 hover:text-brand-900"
            >
              <ArrowUp className="h-4 w-4" />
            </a>
          </div>
        </div>
      </Container>
    </footer>
    </FooterFrame>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: { label: string; href: string }[];
}) {
  return (
    <div>
      <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-gold-300">{title}</h3>
      <ul className="mt-4 space-y-2.5 text-sm">
        {links.map((l) => (
          <li key={l.label}>
            <Link
              href={l.href}
              className="group inline-flex items-center gap-1.5 text-cream/75 transition-all hover:translate-x-1 hover:text-white"
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
